/* global L, omnivore */
/**
 * @module lib/election/election-map
 * @description Initializes a Leaflet-powered election map
 */
import tgamConfig from "config/tgam";
import logger from "utils/logger";
import { electionMapRidingsUrl } from "lib/election-services";

import loadNonCriticalCSS from "utils/load-non-critical-css";
import loadScript from "utils/load-script";
import { getById, getProp, setProp, qs, qsa } from "utils/utils";
import { setupRidingDialog, populateRidingDialog } from "lib/election/riding-cards";
import ee from "lib/eventemitter";
import { errorStateOverlayEnabled, errorStateOverlayDisabled } from "lib/election/error-state";

const debugTag = "[ELECTION-MAP]";
const dialogId = "js-modal-dialog-election-map";
const mapContainerId = "js-election-map-container";
const electionMapEl = `#${mapContainerId}`;
const electionYear = getProp(tgamConfig, "meta.electionYear", "2021");
let parties = ["LIB", "CON", "NDP", "BQ", "GRN"];

if (electionYear === "2019") {
  // PPC was listed explicitly for the 2019 election.
  parties.push("PPC");
} else {
  parties.push("PP");
}

let popup;
let closePopup;
let ridingDialog;


// Will be used by leaflet built-in "setStyle" function to update the styles for every layer/riding
const partyColorMappings = {
  LIB: {
    partial: "#E99092",
    full: "#D71920"
  },
  CON: {
    partial: "#92B7E7",
    full: "#0F2D52"
  },
  NDP: {
    partial: "#FBCDA5",
    full: "#EB7100"
  },
  BQ: {
    partial: "#9ED4D6",
    full: "#63B5BD"
  },
  GRN: {
    partial: "#B1D7AE",
    full: "#35852E"
  },
  PPC: {
    partial: "#B293CC",
    full: "#7337A7"
  },
  PP: {
    partial: "#B293CC",
    full: "#7337A7"
  },
  OTHERS: {
    partial: "#C2C2C2",
    full: "#333333"
  },
  DEFAULT: "#7F7F7F"
};

/**
 * @method init
 * @description Init the election map, load all css and javascript dependencies,fetch the riding data.
 */
export default function init() {
  const electionMap = getById("js-election-map");
  if (!electionMap) {
    logger.info(`${debugTag} map placeholder not found`);
    return;
  }

  ee.on("election:riding-data-failed", () => {
    logger.info(`${debugTag} Map Data failed to load`);
    errorStateOverlayEnabled(electionMapEl);
  });

  // This will allow to display all the ridings with their current status/color almost immediately
  // Subsequent riding color updates will be handled below in `addRidings` function
  const ridingDataReady = new Promise(resolve => {
    ee.once("election:riding-data", resolve);
  });

  import("leaflet" /* webpackChunkName: "leaflet-map" */)
    .then(L => {
      const loadCSS = loadNonCriticalCSS(tgamConfig.env.staticMediaUrl + "/styles/non-critical/vendor/leaflet/dist/leaflet.css");
      const loadLeafletOmnivore = loadScript("//api.tiles.mapbox.com/mapbox.js/plugins/leaflet-omnivore/v0.3.1/leaflet-omnivore.min.js");

      Promise.all([ridingDataReady, loadCSS, loadLeafletOmnivore])
        .then(values => {
          const ridingData = values[0]; // mega riding object
          logger.info(`${debugTag} All election map dependencies and initial data are loaded`);
          addMap(L, ridingData);
        }).catch(reason => {
          logger.info(`${debugTag} No Map Data: ${reason}`);
        });
    });
}

/**
 * @method addMap
 * @description Add the election map to the page.
 * @param {Object} L - Leaflet library.
 * @param {Object} ridingData - mega ridings object.
 */
function addMap(L, ridingData) {
  const clientWidth = document.documentElement.clientWidth;
  const { zoomLevel, minZoom } = getZoom(clientWidth);
  const coordinates = [60.585901, -97.750596];
  const configs = {
    minZoom: minZoom, // Sets how far the map can be zoomed out
    zoomSnap: 0.1, // Enables fractional zoom - valid zoom levels of the map will be 0.1, 0.2, 0.3 and so on (default is 1)
    zoomDelta: 0.75, // Controls how many zoom levels to zoom in/out when using the map zoom buttons
    scrollWheelZoom: false, // Disables map zoom by using the mouse wheel
    zoomControl: true // Allow to change the position of zoom buttons
  };

  const map = L.map("js-election-map", configs).setView(coordinates, zoomLevel);

  L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
    attribution: "Map data &copy; <a href=\"https://www.openstreetmap.org/\">OpenStreetMap</a> contributors, <a href=\"https://creativecommons.org/licenses/by-sa/2.0/\">CC-BY-SA</a>"
  }).addTo(map);

  map.zoomControl.setPosition("bottomright");

  // Adjust the zoom level when viewport changes; the height of the container handled by css
  ee.on("windowResize.throttled", clientRect => {
    const { zoomLevel } = getZoom(clientRect.width);
    map.setZoom(zoomLevel);
  });

  ee.on("tgam-modal-dialog:closed", dialog => {
    if (dialog.getRootElement().id === dialogId) {
      scrollToMap();
    }
  });

  addRidings(map, ridingData);
}

/**
 * @method addRidings
 * @description Add the election ridings to the map.
 * @param {Object} map - map object.
 * @param {Object} ridingData - riding data.
 * @see https://github.com/mapbox/leaflet-omnivore
 * @see https://leafletjs.com/reference-1.5.0.html#geojson-style
 */
function addRidings(map, ridingData) {
  let manualErrorState = getById(mapContainerId).dataset.errorstate;
  logger.info(`${debugTag} manualErrorState: ${manualErrorState}`);
  const geoJsonLayer = L.geoJson(null, {
    // Ridings styles that will be displayed on first page load (the ridings haven't been added to the map yet - see addTo(map) below)
    style: feature => addStyles(feature.properties.FEDNUM, ridingData)
  });

  const electionLayers = omnivore.topojson(electionMapRidingsUrl(electionYear), null, geoJsonLayer)
    // Fires when all layers/ridings are done being initialized
    .on("ready", () => {
      onReady(electionLayers, map);
      setupDialog();
      onRidingDataFetched(electionLayers, ridingData);
    })
    .on("error", error => logger.info(`Ridings are not loaded! ${error}`))
    .addTo(map);

  if (manualErrorState === "true") {
    errorStateOverlayEnabled(electionMapEl);
  } else {
    errorStateOverlayDisabled(electionMapEl);
  }

  // Initialize the popup
  popup = L.popup({ closeButton: false, offset: [0, -7], autoPan: false });

  // Updates the ridings color every time we fetch new election data
  ee.on("election:riding-data", freshRidingData => {
    onRidingDataFetched(electionLayers, freshRidingData);
  });
}

/**
 * Repaint the map's election riding shapes and reinitialize the modal dialog
 * @param {Object} electionLayers
 * @param {Object} ridingData - riding data
 */
function onRidingDataFetched(electionLayers, ridingData) {
  let ridingColor;
  // Before updating the ridings styles check if any layer has the highlighted styles (was clicked)
  electionLayers.eachLayer(layer => {
    const ridingId = layer.feature.properties.FEDNUM;
    // This layer had a border before refresh, so keep it
    if (layer.options.weight === 5) {
      ridingColor = addStyles(ridingId, ridingData, 5);
    } else {
      ridingColor = addStyles(ridingId, ridingData);
    }
    layer.setStyle(ridingColor);
  });

  // Inject the latest data into the riding card template,
  // if a riding card already exists inside the dialog
  if (ridingDialog) {
    const dialogRoot = ridingDialog.getRootElement();
    const existingCard = dialogRoot ? qs(".c-election-riding-card", dialogRoot) : null;
    const rid = existingCard ? getProp(existingCard, "dataset.ridingId", null) : null;

    if (rid) {
      populateRidingDialog(ridingDialog, Number(rid));
    }
  }
}

/**
 * Destroy then reinitialize the modal dialog
 */
function setupDialog() {
  const callbacks = {
    populate: populateDialog
  };
  const options = {
    // Supress the default click event that invokes the dialog
    // When we are dragging the map we basically have a "click" event which opens the dialog,
    // But in our case we need to track if the position of the mouse was changed (dragging) or not (clicking)
    supportDragEvents: true
  };
  const { dialog } = setupRidingDialog(dialogId, callbacks, options);
  ridingDialog = dialog;
}

/**
 * @method onReady
 * @description Manipulate the ridings - register the dialog and process every layer.
 * @param {Object} electionLayers
 * @param {Object} map - map object.
 */
function onReady(electionLayers, map) {
  // Assign a modal dialog id to every riding
  const mapElement = qs("#js-election-map");
  const ridingElements = qsa(".leaflet-interactive", mapElement);
  ridingElements.forEach(addDialogId);

  // Add a popup for every riding
  electionLayers.eachLayer(layer => addLayerEvents(layer, map, electionLayers));
}

/**
 * @method  addLayerEvents
 * @description Initialize popup for every riding and add different layer events.
 * @param {Object} layer - one layer/riding.
 * @param {Object} map - map object.
 * @param {Object} electionLayers - all the ridings.
 */
function addLayerEvents(layer, map, electionLayers) {
  const ridingId = getProp(layer, "feature.properties.FEDNUM", 0);
  // There are more map layers (347) than actual ridings (338) as some ridings can be represented by two territories (an island for example)
  // For example, following ridings have more than one layer: 46010 - 4 layers, 24026 - 2 layers, 24046 - 2 layers, 59009 - 2 layers
  // As the result some paths can get the same id which is not a valid HTML
  // In this case check if the element with the specified id already exists. If yes - append a random number so it stays unique.
  if (!getById(ridingId)) {
    setProp(layer, "_path.id", ridingId);
  } else {
    setProp(layer, "_path.id", `${ridingId}-${Math.floor((Math.random() * 100) + 1)}`);
  }

  // Zoom in and add a white border when any riding is clicked
  layer.on("click", e => selectLayer(e.target, map, electionLayers));

  // Popup events
  layer.on("mousemove", e => {
    popup.setLatLng(e.latlng).setContent(layer.feature.properties.ENNAME).openOn(map);
    window.clearTimeout(closePopup);
  });

  layer.on("mouseout", () => {
    closePopup = setTimeout(() => map.closePopup(), 100);
  });
}

/**
 * @method addDialogId
 * @description Add modal dialog id to every riding.
 * @param {SVGElement} path - that represents every riding.
 */
function addDialogId(path) {
  path.setAttribute("aria-haspopup", "true");
  // SVGElement.dataset is not supported by IE,
  // See https://developer.mozilla.org/en-US/docs/Web/API/SVGElement/dataset
  // Workaround - set custom data attribute via setAttribute.
  path.setAttribute("data-popup", dialogId);
}

/**
 * @method selectLayer
 * @description Adds styles to the riding that was clicked.
 * @param {Object} layer - one layer/riding.
 * @param {Object} map - map object.
 * @param {Object} electionLayers - all the ridings.
 */
function selectLayer(layer, map, electionLayers) {
  const configs = {
    mobile: {
      paddingTopLeft: [20, 20],
      paddingBottomRight: [20, 20]
    },
    desktop: {
      paddingTopLeft: [80, 100],
      paddingBottomRight: [80, 100]
    }
  };

  // Sets a map view that contains the given geographical bounds of the riding with some padding around
  map.fitBounds(layer.getBounds(), document.documentElement.clientWidth < 800 ? configs.mobile : configs.desktop);

  // Reset border styles for all ridings every time we click any riding
  electionLayers.setStyle(getDefaultStyles());

  // Add styles to the clicked riding
  layer.setStyle({ weight: 5 });

  // Send analytics
  ee.trigger("analytics.trackGTMEvent", [{
    feature: "Elections 2021",
    type: "button",
    label: `Riding View: ${layer.feature.properties.ENNAME}`,
    action: "Open riding view (from map)"
  }, "clickEvent"]);
}

/**
 * @method addStyles
 * @description Add styles to the ridings.
 * @param {Number} ridingId - that represents every riding.
 * @param {Object} ridingData - all riding data.
 * @param {Number} weight - of the riding border.
 * @returns {Object} of riding styles.
 */
function addStyles(ridingId, ridingData, weight) {
  const basicStyles = getDefaultStyles();
  basicStyles.fillColor = partyColorMappings.DEFAULT;

  const riding = ridingData[ridingId];
  // We are assuming that the first candidate is always leading
  const leadingParty = getProp(riding, "Cand[0]");
  if (!leadingParty) {
    return basicStyles;
  }
  const ridingColor = getStatusColor(leadingParty);
  basicStyles.fillColor = ridingColor;
  if (weight) {
    basicStyles.weight = weight;
  }
  return basicStyles;
}

/**
 * @method getStatusColor
 * @description Get the color for the riding.
 * @param {Object} leadingParty leading candidate.
 * @returns {String} the color of the riding.
 */
function getStatusColor(leadingParty) {
  const partyName = getProp(leadingParty, "PE", "");
  const status = getRidingStatus(leadingParty);

  // If the leading party is not among the top six parties, display "OTHERS" variant (do not confuse with "DEFAULT")
  const partyColorMapping = parties.includes(partyName) ? partyName : "OTHERS";
  return status !== "none" ? partyColorMappings[partyColorMapping][status] : partyColorMappings.DEFAULT;
}

/**
 * @method getRidingStatus
 * @description Get the status for the specified riding.
 * @param {Object} riding JSON object representing election data for the riding.
 * @returns {String} the status for that riding (full, partial or none).
 */
function getRidingStatus(riding) {
  if (riding.E === "Yes") {
    return "full";
  } else if (riding.V > 0) {
    return "partial";
  }
  return "none";
}

/**
 * @method getZoomConfigs
 * @description Get map Zoom level and Min Zoom depending on the client width.
 * @param {Number} clientWidth
 * @returns {Object} of the zoom configs.
 */
function getZoom(clientWidth) {
  switch (true) {
    case clientWidth <= 350:
      return {
        zoomLevel: 2,
        minZoom: 2
      };
    case clientWidth <= 500:
      return {
        zoomLevel: 2.2,
        minZoom: 2
      };
    case clientWidth <= 700:
      return {
        zoomLevel: 2.9,
        minZoom: 2
      };
    case clientWidth <= 1000:
      return {
        zoomLevel: 3.2,
        minZoom: 3.3
      };
    case clientWidth <= 1280:
      return {
        zoomLevel: 3.7,
        minZoom: 3.5
      };
    default:
      return {
        zoomLevel: 4,
        minZoom: 3.5
      };
  }
}

/**
 * @method getDefaultStyles
 * @descriptions Defaults styles for the riding
 * @returns {Object} with riding default styles
 */
function getDefaultStyles() {
  return {
    color: "#fff",
    weight: 0.3,
    fillOpacity: 0.6,
    smoothFactor: .2,
    opacity: 1,
  };
}

/**
 * @method scrollToMap
 * @description Brings map back into view after closing the modal.
 */
function scrollToMap() {
  const map = getById("js-election-map-container");
  map.scrollIntoView({
    behavior: "smooth",
    block: "center"
  });
}

/**
 * @method hideMap
 * @description Hide the map container element.
 */
export function hideMap() {
  const mapContainer = getById("js-election-map-container");
  mapContainer.classList.add("u-hidden");
}

/**
 * @method populateDialog
 * @description Display the election dialog for the selected riding.
 * @param {Object} dialog - dialog object.
 * @param {Number} ridingID - A riding ID
 */
function populateDialog(dialog, ridingID = null) {
  let rid = ridingID;

  if (!ridingID) {
    const dialogRoot = dialog.getRootElement();
    const openButton = dialog.getOpenButtonThatSpawnedDialog();

    if (!dialogRoot || !openButton) {
      logger.info(`${debugTag} dialogRoot or openButton not found`, dialog);
      return;
    }
    rid = Number(openButton.id.split("-")[0]);
  }
  populateRidingDialog(dialog, rid);
}
